import { Menu, Transition } from "@headlessui/react";
import { EllipsisVerticalIcon, ArrowUpIcon } from "@heroicons/react/24/outline";
import React from "react";
import { NotificationContext } from "../../common/context/notification";
import { classNames } from "../../common/utils/classnames";
import {
  AgentFragment,
  usePromoteAgentToPrimaryMutation,
} from "../../graphql/generated";

const AgentCardActions: React.FC<{
  accountId: string;
  agent: AgentFragment;
  isPrimary: boolean;
  onUpdate?: () => void;
}> = ({ accountId, agent, isPrimary, onUpdate }) => {
  const { notifier } = React.useContext(NotificationContext);
  const [{ error, fetching }, promoteMutation] =
    usePromoteAgentToPrimaryMutation();

  React.useEffect(() => {
    if (error) {
      notifier.error(error);
    }
  }, [error, notifier]);

  const handlePromoteToPrimary = React.useCallback(() => {
    if (!fetching) {
      promoteMutation({ accountId, agentId: agent.id }).then((result) => {
        if (!result.error) {
          notifier.success("Agent promoted to primary");
          onUpdate && onUpdate();
        }
      });
    }
  }, [accountId, agent.id, fetching, promoteMutation, notifier, onUpdate]);

  if (isPrimary) {
    return null;
  }

  return (
    <Menu as="div" className="relative inline-block text-left">
      <div>
        <Menu.Button className="bg-gray-100 rounded-full flex items-center text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-indigo-500">
          <span className="sr-only">Open options</span>
          <EllipsisVerticalIcon className="h-5 w-5" aria-hidden="true" />
        </Menu.Button>
      </div>

      <Transition
        as={React.Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 divide-y divide-gray-100 focus:outline-none z-50">
          <div className="py-1">
            <Menu.Item>
              {({ active }) => (
                <div
                  className={classNames(
                    active ? "bg-gray-100 text-gray-900" : "text-gray-700",
                    "group flex items-center px-4 py-2 text-sm cursor-pointer"
                  )}
                  onClick={handlePromoteToPrimary}
                >
                  <ArrowUpIcon
                    className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500"
                    aria-hidden="true"
                  />
                  Promote to Primary
                </div>
              )}
            </Menu.Item>
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  );
};

export default AgentCardActions;
